import {
	Button,
	Checkbox,
	MuiAlert,
	MuiRadioGroup,
	ResponsiveDrawer,
	Select,
	Spin,
	Typography,
	strIsNullOrEmpty,
	useRenderer
} from '@fox/atoms';
import { PAYMENT_SELECTOR_PREFIX, PaymentSelectorProps } from '@fox/finance';
import { Flex, FlexColumnSpaced, FlexRowSpaced, SpaceBetweenRow } from '@fox/layout';
import { Extension, useSubscribeTopicWithHandler } from 'fox-extensions';
import React, { useReducer, useState } from 'react';
import { useContentContext } from '../../../content-context';
import { useFinancialContext } from '../../../financial-config-context';
import { FINANCIAL_DATA_FETCHED, REFETCH_FINANCIAL } from '../../../topics';
import { useUpdateSubscriptionMutation } from '../../mutation-hooks/mutation-implementations';
import { usePlanOptions, useSubscription } from '../../query-hooks/query-implementations';
import { PlanOptionType, SwiftFoxPlansResult, SwiftFoxSubscription, SwiftFoxSubscriptionDetailed } from '../contracts';
import {
	FeeSummary,
	SubscriptionStatusChip,
	TABLE_BUTTON_STYLE,
	convertMemberPortalCapabilityToSfEnumValue,
	convertMemberPortalToSfMethodEnumValue,
	findSimilarPlanOptions,
	formatCurrency,
	renderDate
} from '../shared';
import { FeeSelector } from './fee-selector';
import { OptionSelector } from './options-selector';
import { WebPaymentType, filterApplicableMethod } from './renew-subscription';
import { SubscriptionState, getInitialState, subscriptionStateReducer } from './subscription-state';

export function PlanSelector({
	plans,
	selectedPlan,
	setSelectedPlan
}: {
	plans: Array<SwiftFoxPlansResult>;
	selectedPlan: SwiftFoxPlansResult;
	setSelectedPlan: (plan: SwiftFoxPlansResult) => void;
}) {
	if (plans.length > 1) {
		return (
			<Select<SwiftFoxPlansResult, false, true, false>
				label="Plan"
				value={selectedPlan}
				onChange={(e, v) => setSelectedPlan(v)}
				options={plans}
				getOptionLabel={p => p.name}
				disableClearable
				style={{ minWidth: '250px' }}
			/>
		);
	} else {
		return <>{selectedPlan.name}</>;
	}
}
function canSubmit(state: SubscriptionState) {
	const hasAutoRenewChanged = state.isAutoRenew != state.initialState.isAutoRenew;
	const hasPlanChanged = state.selectedPlanId !== state.initialState.selectedPlanId;
	const haveOptionsChanged =
		!state.selectedOptions?.every(o => state.initialState.selectedOptions?.find(s => s.id === o.id)) ||
		!state.initialState.selectedOptions?.every(o => state.selectedOptions?.find(s => s.id === o.id));
	const hasFeeChanged = state.selectedFeeId !== state.initialState.selectedFeeId;
	const hasPaymentChanged =
		state.selectedPayment?.['creditCardId'] !== state.initialState.selectedPayment?.['creditCardId'] ||
		state.selectedPayment?.['directDebitId'] !== state.initialState.selectedPayment?.['directDebitId'];
	const hasNominatedFeeChanged = state.nominatedFee !== state.initialState.nominatedFee;
	return (
		(!state.isAutoRenew || state.selectedPayment) &&
		state.selectedPlanId &&
		state.selectedFeeId &&
		(hasPlanChanged ||
			hasAutoRenewChanged ||
			haveOptionsChanged ||
			hasFeeChanged ||
			hasNominatedFeeChanged ||
			(state.isAutoRenew && hasPaymentChanged))
	);
}

function EditSubscription({
	plan,
	plans,
	closeDrawer,
	subscription,
	useLinkedOrganisationData,
	allowedPlanCategories,
	allowedProviders
}: {
	plan: SwiftFoxPlansResult;
	plans: SwiftFoxPlansResult[];
	closeDrawer: () => void;
	subscription: SwiftFoxSubscriptionDetailed;
	useLinkedOrganisationData: boolean;
	allowedProviders?: string[];
	allowedPlanCategories?: string[];
}) {
	const { contentId, requestToken } = useContentContext();
	const { data: plansData, error: plansError, loading: plansLoading } = usePlanOptions({ contentId, requestToken });
	const { providers, getCustomers, customerError, customerLoading } = useFinancialContext();
	const render = useRenderer();
	useSubscribeTopicWithHandler(FINANCIAL_DATA_FETCHED, () => render());

	const customerData = getCustomers(useLinkedOrganisationData);
	const initialState = getInitialState(subscription, plan);
	const [state, dispatch] = useReducer(subscriptionStateReducer, {
		initialState,
		...initialState
	});

	const selectedPlan = plans?.find(p => p.id === state.selectedPlanId);
	const { selectedPayment, isAutoRenew, selectedOptions } = state;

	const {
		error: updateSubscriptionError,
		loading: updateSubscriptionLoading,
		mutate: updateSubscription,
		data: updateSubscriptionResult
	} = useUpdateSubscriptionMutation({ requestToken, topics: [REFETCH_FINANCIAL] });

	if (!strIsNullOrEmpty(customerError)) return <MuiAlert severity="error">{customerError}</MuiAlert>;

	const availableProviders = providers.filter(
		p =>
			(!selectedPlan.requirements ||
				filterApplicableMethod(
					selectedPlan.requirements.filter(r => r.type === 'subscription' && r.fieldId === 'method'),
					p.method
				)) &&
			(allowedProviders?.length ? allowedProviders.includes(p.provider.toLowerCase()) : true)
	);

	const submitAllowed = canSubmit(state) && !updateSubscriptionLoading;

	const selectedFee = selectedPlan.fees.find(f => f.id === state.selectedFeeId);

	if (plansLoading) return <></>;
	if (plansError) return <MuiAlert severity="error">Error loading plan options</MuiAlert>;
	const allowedPlans = plans?.filter(p => {
		const allowedByUmbracoSettings = allowedPlanCategories?.length ? allowedPlanCategories.includes(p.category.toLowerCase()) : true;
		const allowedBySwiftFoxSettings = plansData?.length ? plansData.find(a => a.id === p.id) : true;

		return allowedByUmbracoSettings && allowedBySwiftFoxSettings;
	});

	//it is possible for some plans to have some fees that would be acceptable due to their overridetype matching
	const sameTypePlans =
		allowedPlans?.filter(p => {
			const feesWithOverride = p.fees.filter(f => !!f.overrideType);
			const allFeesDifferentType = feesWithOverride.length
				? feesWithOverride.every(f => f.overrideType !== subscription.planType)
				: false;
			return (
				(p.type === subscription.planType && !allFeesDifferentType) || !!p.fees.find(f => f.overrideType === subscription.planType)
			);
		}) ?? [];

	const allowedPlanOptions = plansData?.find(p => p.id === selectedPlan?.id)?.allowedPlanOptionIds ?? [];

	const availableOptions = selectedPlan?.options?.filter(o => allowedPlanOptions?.includes(o.id) ?? true) ?? [];

	const discounts = availableOptions.filter(a => a.type === PlanOptionType.DISCOUNT);
	const extras = availableOptions.filter(a => a.type === PlanOptionType.EXTRA);
	return (
		<FlexColumnSpaced style={{ padding: '1rem' }}>
			<SpaceBetweenRow>
				<Typography style={{ fontWeight: 'bold' }}>Plan</Typography>
				<Typography>
					{sameTypePlans.length > 1 ? (
						<PlanSelector
							plans={sameTypePlans}
							selectedPlan={selectedPlan}
							setSelectedPlan={plan => {
								const similarFee = plan.fees.find(
									f => f.interval === selectedFee.interval && f.units === selectedFee.units
								);
								const similarOptions = findSimilarPlanOptions({ fee: similarFee, plan, subscription });
								dispatch({ type: 'selectPlanById', data: plan.id });
								dispatch({
									type: 'updatePartial',
									data: {
										selectedFeeId: similarFee.id,
										selectedOptions: similarOptions?.map(o => ({ id: o.id, limit: o.defaultLimit }))
									}
								});
								if (!plan.allowNominated) {
									dispatch({ type: 'updatePartial', data: { nominatedFee: null } });
								}
							}}
						/>
					) : (
						subscription.fee.plan.name
					)}
				</Typography>
			</SpaceBetweenRow>
			<SpaceBetweenRow>
				<Typography style={{ fontWeight: 'bold' }}>Plan cost</Typography>
				<Typography>
					<FeeSummary selectedFee={selectedFee} nominatedFee={state.nominatedFee} />
				</Typography>
			</SpaceBetweenRow>
			<SpaceBetweenRow>
				<Typography style={{ fontWeight: 'bold' }}>Paid to</Typography>
				<Typography>{renderDate(subscription.paidTo)}</Typography>
			</SpaceBetweenRow>
			<SpaceBetweenRow>
				<Typography style={{ fontWeight: 'bold' }}>Arrears</Typography>
				<Typography>{formatCurrency(subscription.arrears, subscription.fee.currency)}</Typography>
			</SpaceBetweenRow>
			<SpaceBetweenRow>
				<Typography style={{ fontWeight: 'bold' }}>Status</Typography>
				<SubscriptionStatusChip status={subscription.status} />
			</SpaceBetweenRow>
			<FeeSelector
				fees={
					selectedPlan.fees?.filter(
						f => subscription.planType === (f.overrideType != null ? f.overrideType : selectedPlan.type)
					) ?? []
				}
				allowNominated={selectedPlan.allowNominated}
				defaultValue={selectedFee?.id ?? null}
				onChange={id => {
					const currentPlan = plans?.find(a => a.id === state.selectedPlanId);
					const fee = currentPlan?.fees?.find(f => f.id === id);

					const similarOptions = findSimilarPlanOptions({ fee, plan, subscription });
					dispatch({
						type: 'updatePartial',
						data: { selectedOptions: similarOptions?.map(o => ({ id: o.id, limit: o.defaultLimit })) }
					});
					dispatch({ type: 'selectFeeById', data: id });
				}}
				nominatedFee={state.nominatedFee}
				updateNominatedFee={(nominatedFee: number) => dispatch({ type: 'updatePartial', data: { nominatedFee } })}
			/>
			<OptionSelector
				title="Extras"
				options={extras}
				selectedPlan={selectedPlan}
				selectedFeeId={selectedFee?.id}
				selectedOptions={selectedOptions}
				toggleOption={(id: string, limit: number) => dispatch({ type: 'toggleOption', data: id, limit })}
			/>
			<OptionSelector
				title="Discounts"
				options={discounts}
				selectedPlan={selectedPlan}
				selectedFeeId={selectedFee?.id}
				selectedOptions={selectedOptions}
				toggleOption={(id: string, limit: number) => dispatch({ type: 'toggleOption', data: id, limit })}
			/>
			<Flex style={{ alignItems: 'center' }}>
				<Typography style={{ fontWeight: 'bold' }}>Auto-renew?</Typography>
				<Checkbox
					size="medium"
					checked={isAutoRenew}
					onChange={(_, checked) => {
						dispatch({ type: 'setIsAutoRenew', data: checked });
						if (!checked) {
							dispatch({ type: 'resetSelectedPayment' });
						}
					}}
				/>
			</Flex>
			{isAutoRenew ? (
				<FlexColumnSpaced>
					<Typography style={{ fontWeight: 'bold' }} variant="subtitle1">
						Select payment method for auto-renew
					</Typography>
					<Spin spinning={customerLoading}>
						{availableProviders?.length > 0 ? (
							<MuiRadioGroup
								value={JSON.stringify(selectedPayment) ?? null}
								name="payment"
								onChange={(_, value) => {
									const newValue = JSON.parse(value);
									dispatch({
										type: 'selectPayment',
										data: newValue.creditCardId
											? {
													creditCardId: newValue.creditCardId,
													method: newValue.method,
													provider: newValue.provider
											  }
											: newValue.directDebitId
											? {
													directDebitId: newValue.directDebitId,
													method: newValue.method,
													provider: newValue.provider
											  }
											: undefined
									});
								}}
							>
								{availableProviders.map((c, i) => {
									const provider = {
										...c,
										capabilities: c.capabilities.map(capability =>
											convertMemberPortalCapabilityToSfEnumValue(capability)
										)
									};
									return (
										<React.Fragment key={i}>
											<Extension<PaymentSelectorProps<WebPaymentType>>
												name={`${PAYMENT_SELECTOR_PREFIX}.${convertMemberPortalToSfMethodEnumValue(c.method)}`}
												props={{
													customers: customerData,
													provider,
													selectedPayment,
													setSelectedPayment: selectedPayment => {
														dispatch({ type: 'selectPayment', data: selectedPayment });
													},
													meta: {
														useLinkedOrganisationData: useLinkedOrganisationData
													}
												}}
											/>
										</React.Fragment>
									);
								})}
							</MuiRadioGroup>
						) : (
							<MuiAlert severity="info">No payment providers available</MuiAlert>
						)}
					</Spin>
				</FlexColumnSpaced>
			) : null}
			<FlexRowSpaced style={{ justifyContent: 'flex-end' }}>
				<Button
					disabled={updateSubscriptionLoading}
					style={{ maxWidth: '100px' }}
					onClick={() => closeDrawer()}
					variant="tertiary"
					color="error"
				>
					Cancel
				</Button>
				<Button
					disabled={!submitAllowed}
					onClick={async () => {
						const result = await updateSubscription({
							contentId,
							autoRenew: isAutoRenew,
							id: subscription.id,
							feeId: selectedFee.id,
							method: selectedPayment?.method,
							provider: selectedPayment?.provider,
							creditCardId: selectedPayment?.['creditCardId'],
							directDebitId: selectedPayment?.['directDebitId'],
							options: selectedOptions,
							useLinkedOrganisationData,
							nominatedFee: state.nominatedFee
						});
						if (result?.id) {
							dispatch({ type: 'resetInitialState' });
						}
					}}
					sx={{ maxWidth: '100px' }}
					variant="primary"
				>
					Update
				</Button>
			</FlexRowSpaced>
			{!strIsNullOrEmpty(updateSubscriptionError) ? <MuiAlert severity="error">{updateSubscriptionError}</MuiAlert> : null}
			{updateSubscriptionResult?.id ? <MuiAlert severity="success">Subscription successfully updated</MuiAlert> : null}
		</FlexColumnSpaced>
	);
}

export function EditSubscriptionDesktop({
	record,
	plans,
	useLinkedOrganisationData,
	allowedPlanCategories,
	allowedProviders
}: {
	record: SwiftFoxSubscription;
	plans: SwiftFoxPlansResult[];
	useLinkedOrganisationData: boolean;
	allowedProviders?: string[];
	allowedPlanCategories?: string[];
}) {
	const { contentId, requestToken } = useContentContext();
	const [open, setOpen] = useState<boolean>(false);

	const {
		error,
		loading,
		data: subscription,
		refetch
	} = useSubscription({
		contentId,
		requestToken,
		subscriptionId: record.id.toString(),
		skip: !record.id
	});

	useSubscribeTopicWithHandler(FINANCIAL_DATA_FETCHED, () => refetch());

	if (error) {
		return <MuiAlert severity="error">{error}</MuiAlert>;
	}
	const plan = plans?.find(p => p.id === subscription?.fee?.planId);
	return (
		<Spin spinning={loading}>
			<Button variant="secondary" sx={TABLE_BUTTON_STYLE} onClick={() => setOpen(true)}>
				Edit details
			</Button>
			<ResponsiveDrawer
				headerVariant="h5"
				placement="right"
				title="Subscription details"
				width={500}
				open={open}
				destroyOnClose
				onClose={() => {
					setOpen(false);
				}}
			>
				<EditSubscription
					closeDrawer={() => {
						setOpen(false);
					}}
					plan={plan}
					plans={plans}
					subscription={subscription}
					useLinkedOrganisationData={useLinkedOrganisationData}
					allowedPlanCategories={allowedPlanCategories}
					allowedProviders={allowedProviders}
				/>
			</ResponsiveDrawer>
		</Spin>
	);
}

export function EditSubscriptionMobile({
	record,
	plans,
	useLinkedOrganisationData,
	allowedPlanCategories,
	allowedProviders
}: {
	record: SwiftFoxSubscription;
	plans: SwiftFoxPlansResult[];
	useLinkedOrganisationData: boolean;
	allowedProviders?: string[];
	allowedPlanCategories?: string[];
}) {
	const { contentId, requestToken } = useContentContext();
	const [open, setOpen] = useState<boolean>(false);

	const {
		error,
		loading,
		data: subscription,
		refetch
	} = useSubscription({
		contentId,
		requestToken,
		subscriptionId: record.id.toString(),
		skip: !record.id
	});

	useSubscribeTopicWithHandler(FINANCIAL_DATA_FETCHED, () => refetch());

	if (error) {
		return <MuiAlert severity="error">{error}</MuiAlert>;
	}
	const plan = plans?.find(p => p.id === subscription?.fee?.planId);

	return (
		<Spin spinning={loading}>
			<Button variant="secondary" sx={TABLE_BUTTON_STYLE} onClick={() => setOpen(true)}>
				Edit details
			</Button>
			<ResponsiveDrawer
				headerVariant="h5"
				placement="bottom"
				title="Subscription details"
				width="100%"
				height="100%"
				style={{ height: '100%' }}
				destroyOnClose
				open={open}
				onClose={() => {
					setOpen(false);
				}}
			>
				<EditSubscription
					closeDrawer={() => {
						setOpen(false);
					}}
					plan={plan}
					plans={plans}
					subscription={subscription}
					useLinkedOrganisationData={useLinkedOrganisationData}
					allowedPlanCategories={allowedPlanCategories}
					allowedProviders={allowedProviders}
				/>
			</ResponsiveDrawer>
		</Spin>
	);
}
